import React, { useEffect, useRef, useState } from 'react';
import {
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  Paper,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import EditTaskIcon from '@material-ui/icons/BorderColor';
import Arrow from '@material-ui/icons/CompareArrowsOutlined';
import useStyles from './styles';
import Pagination from '../../../../components/Pagination';
import { getTaskInstances } from '../../../../requests/api/taskInstance';

const AssignedUsers = ({ taskId }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const taskURLPath = '/tasks';
  const classes = useStyles();
  const history = useHistory();
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [rowsToShow, setRowsToShow] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ users: [], locations: [] });
  const filterOptionsLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const [taskIQuantity, setTaskIQuantity] = useState(0);
  const tableHeaders = [
    { id: 'userfullName', label: t('assignedUsers.tableHeaders.userfullName') },
    { id: 'userEmail', label: 'Email' },
    { id: 'resetScale', label: t('assignedUsers.tableHeaders.resetScale') },
    { id: 'expectedLocationResponses', label: '#' },
    { id: 'location', label: t('assignedUsers.tableHeaders.location') },
    { id: 'categoryCode', label: t('assignedUsers.tableHeaders.category') },
    {
      id: 'productGroupId',
      label: t('assignedUsers.tableHeaders.productGroupId'),
    },
    { id: 'editButton', label: t('assignedUsers.tableHeaders.edit') },
  ];
  const [userFilter, setUserFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const parseResetScale = (value) => {
    // TODO: arreglar, quarterly se está escribiendo mal en la db
    if (!value) return '-';
    const resetScale = value === 'quartely' ? 'quarterly' : value;
    return t(`task.association.resetTimes.${resetScale}`);
  };

  useEffect(() => {
    filterOptionsLoaded.current = false;
  }, [taskId]);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    const fetchAssignedUsers = async () => {
      const includeFilters = !filterOptionsLoaded.current;
      const {
        data,
      } = await getTaskInstances(
        taskId,
        page,
        userFilter,
        locationFilter,
        includeFilters,
      );
      if (!cancelled && data.status === 200) {
        const {
          count, rows, filters,
        } = data.data.taskInstances;
        setRowsToShow(rows);
        if (filters) {
          setFilterOptions(filters);
          filterOptionsLoaded.current = true;
        }
        setTaskIQuantity(count);
        setLoading(false);
      }
    };
    fetchAssignedUsers();
    return () => {
      cancelled = true;
    };
  }, [page, taskId, userFilter, locationFilter]);

  const table = () => {
    const content = (
      <>
        {rowsToShow.length === 0 ? (
          <TableRow>
            <TableCell colSpan={tableHeaders.length} align="center">
              <Typography>
                {t(
                  filterOptions.users.length === 0
                    ? 'assignedUsers.noUsers'
                    : 'assignedUsers.emptyTable',
                )}
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          rowsToShow.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.User.fullName}</TableCell>
              <TableCell>{row.User.email}</TableCell>
              <TableCell>{parseResetScale(row.resetScale)}</TableCell>
              <TableCell>{row.expectedLocationResponses}</TableCell>
              <TableCell>{row.Location?.name || '---'}</TableCell>
              <TableCell>{row.categoryCode}</TableCell>
              <TableCell>{row.productGroupId}</TableCell>
              <TableCell>
                <IconButton
                  onClick={() => history.push(
                    `${taskURLPath}/${taskId}/associations/${row.id}/edit`,
                  )
                  }
                >
                  <EditIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))
        )}
      </>
    );
    return (
      <>
        <TableContainer component={Paper}>
          <Table
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <TableHead>
              <TableRow>
                {tableHeaders.map((tableHeader) => (
                  <TableCell
                    key={tableHeader.id}
                    className={classes.tableHeader}
                    padding="normal"
                  >
                    <Typography className={classes.semiBold}>
                      {tableHeader.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      py={2}
                    >
                      <CircularProgress />
                      <Typography variant="h6">{t('task.loading')}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                content
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          setPage={setPage}
          page={page}
          rowsPerPage={rowsPerPage}
          quantity={taskIQuantity}
        />
      </>
    );
  };
  const buttons = (
    <Box display="flex" justifyContent="space-between">
      <Typography variant="h1" className={classes.title} gutterBottom>
        {t('assignedUsers.title')}
      </Typography>
      <Box ml={2}>
        <IconButton
          component={Link}
          to={`${taskURLPath}/${taskId}/associations`}
        >
          <Arrow fontSize="small" titleAccess="Crear asociaciones" />
        </IconButton>
        <IconButton
          component={Link}
          to={`${taskURLPath}/${taskId}/associations/edit`}
        >
          <EditTaskIcon titleAccess="Editar Asociaciones" />
        </IconButton>
      </Box>
    </Box>
  );
  return (
    <>
      {buttons}

      {/* Filters */}
      <Box display="flex" flexDirection="row" mb={2} className={styles.row}>
        <TextField
          className={styles.filterField}
          select
          label={t('assignedUsers.tableHeaders.userfullName')}
          size="small"
          variant="outlined"
          value={userFilter}
          onChange={(e) => {
            setPage(0);
            setUserFilter(e.target.value);
          }}
        >
          <MenuItem value="">{t('assignedUsers.all')}</MenuItem>
          {filterOptions.users.map(({ id, fullName }) => (
            <MenuItem key={id} value={id}>
              {fullName}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          className={styles.filterField}
          select
          label={t('assignedUsers.tableHeaders.location')}
          size="small"
          variant="outlined"
          value={locationFilter}
          onChange={(e) => {
            setPage(0);
            setLocationFilter(e.target.value);
          }}
        >
          <MenuItem value="">{t('assignedUsers.all')}</MenuItem>
          {filterOptions.locations.map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {table()}
    </>
  );
};

export default AssignedUsers;
